import {
  Col,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
} from "antd";
import { Rule } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import { bannerApi } from "api/banner.api";
import { RichTextEditor } from "components/Editor/RichTextEditor";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useEffect, useRef, useState } from "react";
import { ModalStatus } from "types/modal";
import {
  Banner,
  BannerContentType,
  BannerContentTypeTrans,
  BannerType,
  BannerTypeTrans,
} from "types/banner";
import { observer } from "mobx-react";
import { areaStore } from "store/areaStore";
import { businessTypeStore } from "store/businessTypeStore";
import { Shop } from "types/shop";
import { shopApi } from "api/shop.api";
import { debounce } from "lodash";
import { Product } from "types/product";
import { productApi } from "api/product.api";
import { DefaultOptionType } from "antd/lib/select";

const rules: Rule[] = [{ required: true }];
const { Option } = Select;

interface BannerForm extends Banner {
  areaId: number;
  productId: number;
  shopId: number;
  imageMobile: string;
  businessTypeId?: number;
  contentType?: BannerContentType;
}

export const BannerModal = observer(
  ({
    visible,
    status,
    banner,
    onClose,
    onSubmitOk,
  }: {
    visible: boolean;
    status: ModalStatus;
    banner: Partial<Banner>;
    onClose: () => void;
    onSubmitOk: () => void;
  }) => {
    const [form] = Form.useForm<BannerForm>();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState("");
    const [shops, setShops] = useState<DefaultOptionType[]>([]);
    const [products, setProducts] = useState<DefaultOptionType[]>([]);
    const [fetching, setFetching] = useState(false);
    const [formContentType, setFormContentType] = useState(
      BannerContentType.Content
    );
    const editorRef = useRef<{ setContent: (content: string) => void }>(null);

    useEffect(() => {
      fetchDataShop();
    }, []);

    useEffect(() => {
      if (status == "create" && visible) {
        form.resetFields();
      } else {
      }
      setContent(banner.content || "");
    }, [visible, status]);

    useEffect(() => {
      form.setFieldsValue({
        ...banner,
        areaId: banner.area?.id,
        productId: banner.product?.id,
        shopId: banner.shop?.id,
      });
      return () => {};
    }, [banner]);

    const fetchDataShop = async (search = "") => {
      setFetching(true);
      const res = await shopApi.findAll({ page: 1, limit: 20, search });
      setFetching(false);

      const dataShops: Shop[] = res.data.shops;
      const options = dataShops.map((e) => ({
        value: e.id,
        label: e.name,
      }));
      setShops([{ value: 0, label: "Không chọn" }, ...options]);
    };

    const fetchDataProduct = async (search = "") => {
      const shopId = form.getFieldValue("shopId");
      const res = await productApi.findAll({
        page: 1,
        limit: 20,
        search,
        shopId,
      });

      const dataProducts: Product[] = res.data.products;
      const options = dataProducts.map((e) => ({
        value: e.id,
        label: e.name,
      }));
      setProducts([{ value: 0, label: "Không chọn" }, ...options]);
    };

    const onShopChange = () => {
      form.setFieldsValue({ productId: 0 });
      fetchDataProduct("");
    };

    const onSearchShop = useRef(debounce(fetchDataShop, 500));

    const onSearchProduct = useRef(debounce(fetchDataProduct, 500));

    const createData = async () => {
      const valid = await form.validateFields();
      let businessTypeId = form.getFieldValue("businessTypeId");
      if (form.getFieldValue("type") != BannerType.BusinessType) {
        businessTypeId = 0;
      }
      const data = {
        banner: {
          ...form.getFieldsValue(),
          content,
        },
        areaId: form.getFieldValue("areaId"),
        productId: form.getFieldValue("productId"),
        shopId: form.getFieldValue("shopId"),
        businessTypeId,
      };

      setLoading(true);
      try {
        const res = await bannerApi.create(data);
        message.success("Tạo mới thành công!");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    const updateData = async () => {
      const valid = await form.validateFields();
      console.log("form.getFieldsValue()", form.getFieldsValue());
      let businessTypeId = form.getFieldValue("businessTypeId");
      if (form.getFieldValue("type") != BannerType.BusinessType) {
        businessTypeId = 0;
      }

      const data = {
        banner: {
          ...form.getFieldsValue(),
          content,
        },
        areaId: form.getFieldValue("areaId"),
        productId: form.getFieldValue("productId"),
        shopId: form.getFieldValue("shopId"),
        businessTypeId,
      };

      setLoading(true);
      try {
        const res = await bannerApi.update(banner?.id || 0, data);
        message.success("Cập nhật thành công");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    return (
      (<Modal
        onCancel={onClose}
        open={visible}
        title={status == "create" ? "Tạo mới banner" : "Cập nhật banner"}
        style={{ top: 20 }}
        width={1200}
        cancelText="Đóng"
        okText="Lưu"
        confirmLoading={loading}
        onOk={() => {
          status == "create" ? createData() : updateData();
        }}
      >
        <Form
          layout="vertical"
          initialValues={{ contentType: formContentType }}
          form={form}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Tiêu đề" name="title">
                <Input placeholder="" />
              </Form.Item>

              <Form.Item label="Loại" name="type" rules={rules}>
                <Select style={{ width: "100%" }}>
                  {Object.values(BannerType).map((type) => (
                    <Option key={type} value={type}>
                      {BannerTypeTrans[type]}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {() => (
                  <>
                    {form.getFieldValue("type") == BannerType.BusinessType && (
                      <Form.Item
                        rules={rules}
                        label="Loại kinh doanh"
                        name="businessTypeId"
                      >
                        <Select style={{ width: "100%" }}>
                          {businessTypeStore.list.map((e) => (
                            <Option key={e.id} value={e.id}>
                              {e.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </>
                )}
              </Form.Item>

              {/* <Form.Item label="Khu vực" name="areaId">
                <Select style={{ width: "100%" }}>
                  <Option value={0}>Không chọn</Option>
                  {areaStore.list.map((e) => (
                    <Option key={e.id} value={e.id}>
                      {e.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}

              <Form.Item shouldUpdate={true}>
                {() => {
                  return (
                    <Form.Item
                      label={"Banner màn hình máy tính (Kích thước: 1110x170)"}
                      required
                      rules={rules}
                      name="image"
                    >
                      <SingleImageUpload
                        width={300}
                        height={150}
                        onUploadOk={(path: string) => {
                          console.log("onUploadOk:", path);
                          form.setFieldsValue({
                            image: path,
                          });
                        }}
                        imageUrl={form.getFieldValue("image")}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item shouldUpdate={true}>
                {() => {
                  return (
                    <Form.Item
                      label="Banner màn hình điện thoại (Kích thước: 400x400)"
                      required
                      rules={rules}
                      name="imageMobile"
                    >
                      <SingleImageUpload
                        width={300}
                        height={150}
                        onUploadOk={(path: string) => {
                          console.log("onUploadOk:", path);
                          form.setFieldsValue({
                            imageMobile: path,
                          });
                        }}
                        imageUrl={form.getFieldValue("imageMobile")}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>

            <Col span={16}>
              <Form.Item label="Loại nội dung" name="contentType">
                <Radio.Group value={formContentType}>
                  {Object.values(BannerContentType).map((type) => (
                    <Radio key={type} value={type}>
                      {BannerContentTypeTrans[type]}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {() => {
                  if (
                    form.getFieldValue("contentType") == BannerContentType.Link
                  ) {
                    return (
                      <Form.Item rules={rules} label="Link" name="link">
                        <Input placeholder="" />
                      </Form.Item>
                    );
                  }
                  if (
                    form.getFieldValue("contentType") == BannerContentType.Shop
                  ) {
                    return (
                      <Form.Item
                        rules={rules}
                        label="Doanh nghiệp"
                        name="shopId"
                      >
                        <Select
                          style={{ width: "100%" }}
                          onSearch={onSearchShop.current}
                          showSearch
                          notFoundContent={
                            fetching ? <Spin size="small" /> : null
                          }
                          options={shops}
                          filterOption={false}
                        />
                      </Form.Item>
                    );
                  }
                  if (
                    form.getFieldValue("contentType") ==
                    BannerContentType.Product
                  ) {
                    return (
                      <>
                        <Form.Item
                          rules={rules}
                          label="Doanh nghiệp"
                          name="shopId"
                        >
                          <Select
                            style={{ width: "100%" }}
                            onSearch={onSearchShop.current}
                            showSearch
                            notFoundContent={
                              fetching ? <Spin size="small" /> : null
                            }
                            options={shops}
                            filterOption={false}
                            onChange={onShopChange}
                          />
                        </Form.Item>

                        <Form.Item
                          rules={rules}
                          label="Sản phẩm"
                          name="productId"
                        >
                          <Select
                            style={{ width: "100%" }}
                            onSearch={onSearchProduct.current}
                            showSearch
                            notFoundContent={
                              fetching ? <Spin size="small" /> : null
                            }
                            options={products}
                            filterOption={false}
                          />
                        </Form.Item>
                      </>
                    );
                  }
                  return (
                    <Form.Item required label="Nội dung" name="content">
                      <RichTextEditor
                        ref={editorRef}
                        onInit={() => {
                          editorRef.current?.setContent(content);
                        }}
                        onChange={setContent}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>)
    );
  }
);
